import { Badge } from '@cloudflare/kumo';
import { useMemo } from 'react';
import { palette } from '../../lib/palette';
import type { Deal, Stage } from '../../services/funnelApi';
import { DealCard } from './DealCard';
import { SkeletonKanbanCards } from '../ui/Skeleton';

type Props = {
  stage: Stage;
  /** Todos os estágios do pipeline (para o menu "Mover para…" dos cards) */
  stages: Stage[];
  deals: Deal[];
  selectedDealId: string | null;
  onSelectDeal: (dealId: string) => void;
  onDropDeal: (dealId: string, toStageId: string) => void;
  /** Mover pelo menu do card (teclado) — devolve o foco ao card */
  onMoveDeal: (dealId: string, toStageId: string) => void;
  isDragOverStageId: string | null;
  setDragOverStageId: (id: string | null) => void;
  loading?: boolean;
  hintId?: string;
};

/**
 * Coluna de um stage. Calcula total de valor e contagem para o cabeçalho e
 * serve de alvo de DnD (HTML5 nativo). Não conhece a lista completa — recebe
 * apenas os deals da coluna.
 */
export function StageColumn({
  stage,
  stages,
  deals,
  selectedDealId,
  onSelectDeal,
  onDropDeal,
  onMoveDeal,
  isDragOverStageId,
  setDragOverStageId,
  loading,
  hintId,
}: Props) {
  const total = useMemo(
    () => deals.reduce((acc, d) => acc + (Number(d.amount) || 0), 0),
    [deals],
  );

  // Cor do estágio: a cadastrada no pipeline ou, sem ela, a da paleta do Cloudscape
  const borderColor = stage.color || (stage.isWon ? palette.green.base : stage.isLost ? palette.red.base : palette.gray.light1);
  const over = isDragOverStageId === stage.id;
  const headingId = `funil-column-title-${stage.id}`;

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!over) setDragOverStageId(stage.id);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    // só limpa quando sai do container, não quando passa por cima de um filho
    if (e.currentTarget === e.target) setDragOverStageId(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('text/deal-id');
    setDragOverStageId(null);
    if (dealId) onDropDeal(dealId, stage.id);
  };

  return (
    <section
      role="group"
      aria-label={`Estágio: ${stage.name}`}
      className={`funil-column${over ? ' is-over' : ''}`}
      style={{ borderTopColor: borderColor }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      aria-labelledby={headingId}
      data-testid={`funil-column-${stage.id}`}
      data-stage-id={stage.id}
    >
      <header className="funil-column-header">
        <h3 className="text-lg font-semibold font-semibold" id={headingId}>{stage.name}</h3>
        <Badge variant="neutral" data-testid={`funil-column-count-${stage.id}`}>
          {deals.length}
        </Badge>
      </header>
      <span className="text-xs text-kumo-subtle">
        {total > 0
          ? total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
          : 'Sem valor'}
      </span>
      <ul className="funil-column-list" data-testid={`funil-column-list-${stage.id}`}>
        {loading && deals.length === 0 ? (
          <li className="funil-empty"><SkeletonKanbanCards /></li>
        ) : deals.length === 0 ? (
          <li className="funil-empty"><span className="text-xs text-kumo-subtle">Arraste um card aqui</span></li>
        ) : (
          deals.map((d) => (
            <DealCard
              key={d.id}
              deal={d}
              selected={selectedDealId === d.id}
              onSelect={onSelectDeal}
              stages={stages}
              onMove={onMoveDeal}
              hintId={hintId}
            />
          ))
        )}
      </ul>
    </section>
  );
}
